import axios from 'axios';

export const api = axios.create({
  baseURL: '',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * What a dead session used to look like.
 *
 * AuthContext reads /api/auth/me once on mount, so when the session cookie
 * expired later it still reported `authenticated: true`. Every subsequent
 * query 401'd, react-query retried once, and each page fell through to its
 * own empty branch — leaving a fully-chromed console with no characters, no
 * vaults and no fittings, and nothing anywhere saying "log in again". The
 * app looked broken rather than logged out.
 *
 * Subscribers are notified once per transition rather than per failed
 * request, so a page with six queries in flight doesn't fire six redirects.
 */
type UnauthorizedHandler = () => void;

const unauthorizedHandlers = new Set<UnauthorizedHandler>();
let sessionIsDead = false;

/** Register a reaction to the session going away. Returns an unsubscribe. */
export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler);
  return () => unauthorizedHandlers.delete(handler);
}

/** Called after a successful re-auth so the next 401 is reported again. */
export function resetUnauthorizedLatch(): void {
  sessionIsDead = false;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      // /api/auth/me answering 401 is the normal "nobody is logged in yet"
      // case on the login gate — not a session that died under someone.
      const isAuthProbe = String(error?.config?.url ?? '').includes('/api/auth/me');
      if (!isAuthProbe && !sessionIsDead) {
        sessionIsDead = true;
        for (const handler of unauthorizedHandlers) {
          try {
            handler();
          } catch (err) {
            console.error('unauthorized handler failed:', err);
          }
        }
      }
    }

    return Promise.reject(error);
  },
);
